import React, { useState, useEffect } from 'react';
import { checkAssessmentAvailability } from '../utils/dataHandler';

const LandingPage = ({ onStart, loading, error }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    userName: '',
    companyIndustry: '',
    userDepartment: ''
  });

  const [errors, setErrors] = useState({});
  const [assessmentAvailable, setAssessmentAvailable] = useState(true);
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const [checkingAvailability, setCheckingAvailability] = useState(true);

  useEffect(() => {
    // Check if assessment is available when component loads
    const checkAvailability = async () => {
      try {
        const result = await checkAssessmentAvailability();
        setAssessmentAvailable(result.available);
        if (!result.available) {
          setAvailabilityMessage(result.message);
        }
      } catch (error) {
        setAssessmentAvailable(false);
        setAvailabilityMessage('Assessment is temporarily unavailable. Please contact info@thedewpointgroup.com for assistance.');
      } finally {
        setCheckingAvailability(false);
      }
    };

    checkAvailability();
  }, []);

  const industryOptions = [
    'Retail',
    'Pharmaceutical',
    'Insurance',
    'Technology',
    'Healthcare',
    'Financial Services',
    'Manufacturing',
    'Education',
    'Government',
    'Non-profit',
    'Consulting',
    'Real Estate',
    'Transportation',
    'Energy',
    'Media & Entertainment',
    'Other'
  ];

  const departmentOptions = [
    'Marketing',
    'Sales',
    'IT',
    'HR',
    'Finance',
    'Operations',
    'Customer Service',
    'Product Management',
    'Engineering',
    'Data & Analytics',
    'Strategy',
    'Legal',
    'Procurement',
    'Quality Assurance',
    'Research & Development',
    'Other'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company Name is required';
    }
    
    if (!formData.userName.trim()) {
      newErrors.userName = 'User Name is required';
    }
    
    if (!formData.companyIndustry) {
      newErrors.companyIndustry = 'Company Industry is required';
    }
    
    if (!formData.userDepartment) {
      newErrors.userDepartment = 'User Department is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onStart(formData);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/logos/DP LOGO - ON WHT.png" 
            alt="Dewpoint Group Logo" 
            className="h-16 w-auto mx-auto mb-4"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <h1 className="text-3xl font-bold gradient-text mb-2">
            AI Maturity Assessment
          </h1>
          <p className="text-gray-600 text-lg">Lite Version</p>
          <p className="text-gray-500 text-sm mt-2">
            Quick assessment to evaluate your organization's AI readiness
          </p>
        </div>

        {/* Form */}
        <div className="glass-card rounded-2xl p-8">
          {/* Availability Check Loading */}
          {checkingAvailability && (
            <div className="mb-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Checking assessment availability...</p>
            </div>
          )}

          {/* Assessment Not Available */}
          {!checkingAvailability && !assessmentAvailable && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Assessment Temporarily Unavailable</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{availabilityMessage}</p>
                  </div>
                  <div className="mt-4">
                    <a
                      href="mailto:info@thedewpointgroup.com?subject=AI%20Assessment%20Request&body=Hello,%0D%0A%0D%0AI%20would%20like%20to%20request%20access%20to%20the%20AI%20Maturity%20Assessment.%0D%0A%0D%0AThank%20you!"
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    >
                      <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Contact Administrator
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Assessment Available - Show Form */}
          {!checkingAvailability && assessmentAvailable && (
            <>
              {/* Error Display */}
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Unable to Start Assessment</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{error}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                className={`input-field ${errors.companyName ? 'border-red-500' : ''}`}
                placeholder="Enter your company name"
                disabled={loading}
              />
              {errors.companyName && (
                <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
              )}
            </div>

            {/* User Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Name (Full) *
              </label>
              <input
                type="text"
                value={formData.userName}
                onChange={(e) => handleInputChange('userName', e.target.value)}
                className={`input-field ${errors.userName ? 'border-red-500' : ''}`}
                placeholder="Enter your full name"
                disabled={loading}
              />
              {errors.userName && (
                <p className="text-red-500 text-sm mt-1">{errors.userName}</p>
              )}
            </div>

            {/* Company Industry */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Industry *
              </label>
              <select
                value={formData.companyIndustry}
                onChange={(e) => handleInputChange('companyIndustry', e.target.value)}
                className={`input-field ${errors.companyIndustry ? 'border-red-500' : ''}`}
                disabled={loading}
              >
                <option value="">Select your industry</option>
                {industryOptions.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
              {errors.companyIndustry && (
                <p className="text-red-500 text-sm mt-1">{errors.companyIndustry}</p>
              )}
            </div>

            {/* User Department */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Department *
              </label>
              <select
                value={formData.userDepartment}
                onChange={(e) => handleInputChange('userDepartment', e.target.value)}
                className={`input-field ${errors.userDepartment ? 'border-red-500' : ''}`}
                disabled={loading}
              >
                <option value="">Select your department</option>
                {departmentOptions.map(department => (
                  <option key={department} value={department}>{department}</option>
                ))}
              </select>
              {errors.userDepartment && (
                <p className="text-red-500 text-sm mt-1">{errors.userDepartment}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Starting Assessment...' : 'Start Assessment'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              This assessment takes approximately 10-15 minutes to complete.
              <br />
              You can save your progress and return later.
            </p>
          </div>
          </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-gray-400">
            Powered by <span className="font-medium">Dewpoint Group</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;